import { useContext, useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { Context } from "../../context";
import { jsPDF } from "jspdf";
import {
    Button,
    createTableColumn,
    makeStyles,
    mergeClasses,
} from "@fluentui/react-components";
import * as echarts from "echarts";
import { useParams, useSearchParams } from "react-router-dom";
import {
    toDateString,
    toDateWeekdayTimeString,
    yyyymmddCn,
} from "../../date_utils";
import { cvtPriority, formatFloat, idString, mapPart } from "../../db_utils";
import { listDefectionOfTask } from "../../services/defection_service";
import { listDefDefection } from "../../services/def_defection_service";
import * as _ from "lodash";
import { SimpleGrid } from "../../components/simple_grid";
import { parseDateAsPath } from "../../db_utils";

const useStyles = makeStyles({
    grid: {
        "--strokeWidthThin": "0px",
        "font-size": "12px",
    },
});

const getFrameDataUrl = async (frameDataId) => {
    try {
        const apiEndpoint = process.env.REACT_APP_API_ENDPOINT || "";
        const response = await fetch(`${apiEndpoint}/api/file/url/framedata/${frameDataId}`, {
            headers: {
                "Authorization": `Bearer ${localStorage.getItem('token')}`
            }
        });
        const data = await response.json();
        return data.url;
    } catch (error) {
        console.error('Error getting frame data URL:', error);
        return null;
    }
};

const appendQueryParams = (url, params) => {
    if (!url || /^(data:|blob:)/i.test(url)) {
        return url;
    }

    const searchParams = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") {
            return;
        }
        searchParams.set(key, String(value));
    });

    const query = searchParams.toString();
    if (!query) {
        return url;
    }

    const hashIndex = url.indexOf("#");
    const base = hashIndex >= 0 ? url.slice(0, hashIndex) : url;
    const hash = hashIndex >= 0 ? url.slice(hashIndex) : "";
    return `${base}${base.includes("?") ? "&" : "?"}${query}${hash}`;
};

const getReportImageUrl = (url) => {
    if (!url) {
        return null;
    }

    const trimmed = String(url).trim();
    if (/^(data:|blob:)/i.test(trimmed)) {
        return trimmed;
    }

    if (/^https?:/i.test(trimmed)) {
        try {
            const parsed = new URL(trimmed);
            if (
                ["localhost", "127.0.0.1"].includes(parsed.hostname) &&
                parsed.pathname.startsWith("/api/storage/")
            ) {
                return `${parsed.pathname}${parsed.search}`;
            }
            if (
                parsed.origin === window.location.origin &&
                parsed.pathname.startsWith("/api/storage/")
            ) {
                return `${parsed.pathname}${parsed.search}`;
            }
        } catch (error) {
            return trimmed;
        }
        return trimmed;
    }

    if (trimmed.startsWith("/api/storage/")) {
        return trimmed;
    }

    if (trimmed.startsWith("/local-storage/") || trimmed.startsWith("/restructured/")) {
        return `/api/storage${trimmed}`;
    }

    if (trimmed.startsWith("/")) {
        return trimmed;
    }

    return `/api/storage/${trimmed}`;
};

const getPositiveNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
};

const hasDefectionBox = (defection) => {
    return (
        getPositiveNumber(defection?.width) !== null &&
        getPositiveNumber(defection?.height) !== null
    );
};

const buildReportImageUrl = (defection) => {
    const originalUrl = getReportImageUrl(defection?.inspection_image?.file_path);
    const thumbnailUrl = getReportImageUrl(defection?.inspection_image?.thumbnail_url);
    const canCrop = hasDefectionBox(defection) && !!originalUrl;
    const sourceUrl = canCrop ? originalUrl : thumbnailUrl || originalUrl;

    if (!sourceUrl) {
        return null;
    }

    if (!sourceUrl.startsWith("/api/storage/")) {
        return sourceUrl;
    }

    const params = {
        inline: true,
        maxWidth: canCrop ? 1200 : 900,
        quality: canCrop ? 84 : 78,
        drawBox: canCrop,
    };

    if (canCrop) {
        params.x = defection.x;
        params.y = defection.y;
        params.width = defection.width;
        params.height = defection.height;
        params.margin = 0.8;

        const basisWidth = getPositiveNumber(defection?.inspection_image?.image_width);
        const basisHeight = getPositiveNumber(defection?.inspection_image?.image_height);
        if (basisWidth && basisHeight) {
            params.basisWidth = basisWidth;
            params.basisHeight = basisHeight;
        }
    }

    return appendQueryParams(sourceUrl, params);
};

// TODO: This should use a backend API endpoint instead of direct OSS access
// Currently using placeholder - needs backend API implementation
export const pdfReportUrl = (task_id, oss) => {
    // This will need to be replaced with a proper backend API call
    // For now, commenting out the hardcoded robocar bucket
    console.warn('pdfReportUrl needs backend API implementation');
    return null; // Temporarily disable direct OSS access
};

// await loadFont("/css/fonts/exo-2-v9-latin-ext_latin-italic.ttf", "Exo-2", "italic", 400);
// await loadFont("/css/fonts/exo-2-v9-latin-ext_latin-regular.ttf", "Exo-2", "normal", 400);
// await loadFont("/css/fonts/exo-2-v9-latin-ext_latin-500.ttf", "Exo-2", "normal", 500);
// await loadFont("/css/fonts/exo-2-v9-latin-ext_latin-500italic.ttf", "Exo-2", "italic", 500);
async function loadFont(src, name, style, weight) {
    let fontBytes = await fetch(src).then((res) => res.text());
    console.log("font loaded");
    // let fontBytes = await fetch(src).then((res) => res.arrayBuffer());
    // fontBytes = new Uint8Array(fontBytes);

    var filename = src.split("\\").pop().split("/").pop();
    // var base64String = btoa(
    //     String.fromCharCode.apply(null, new Uint8Array(fontBytes))
    // );

    // return new Promise((resolve, reject) => {
    var callAddFont = function () {
        // this.addFileToVFS(filename, base64String);
        // console.log("this:", this);
        this.addFileToVFS(filename, fontBytes);
        // console.log("addFileToVSF ok");
        this.addFont(filename, name, style);
        // console.log("addFont ok:", name);
        // this.setFont(name);
        // console.log("setFont ok:", name);
        // resolve();
    };
    jsPDF.API.events.push(["addFonts", callAddFont]);
    const doc = new jsPDF({
        unit: "pt",
        format: "a4",
    });
    let size = doc.internal.pageSize;
    let w = size.getWidth();
    let h = size.getHeight();
    console.log("w:", w, "h:", h);
    // });
}

export let pdfInitState = null;

export const initializePdf = async () => {
    if (pdfInitState == "loaded" || pdfInitState == "loading") {
        return;
    }

    pdfInitState = "loading";
    try {
        await loadFont(
            "/fonts/MicrosoftYaHei.ttf.base64",
            "yahei",
            "normal",
            500,
        );
    } catch (e) {
        console.log("loadFont error:", e);
    }
    pdfInitState = "loaded";
};

export const usePdfSize = () => {
    let ret = useMemo(() => {
        const doc = new jsPDF({
            unit: "pt",
            format: "a4",
        });
        let size = doc.internal.pageSize;
        let pageWidth = size.getWidth();
        let pageHeight = size.getHeight();
        console.log("[pt] pageWidth=", pageWidth, "pageHeight=", pageHeight);
        return { pageWidth, pageHeight };
    }, []);
    return ret;
};

export const SingleReportPage = ({}) => {
    const { taskId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const hideExport = !!searchParams.get("hideExport");
    return (
        <SingleReport
            taskId={taskId}
            showExport={!hideExport}
            autoExport={false}
        />
    );
};

export const useExportTaskReport = () => {
    const [inner, setInner] = useState(null);

    useEffect(() => {
        initializePdf();
    }, []);

    const exportTaskReport = (taskId) => {
        return new Promise((resolve, reject) => {
            const onExported = () => {
                setInner(null);
                resolve();
            };
            let inner = (
                <SingleReport
                    taskId={taskId}
                    showExport={false}
                    autoExport={true}
                    onExported={onExported}
                />
            );
            setInner(inner);
        });
    };

    const taskReportView = createPortal(
        <div className="absolute left-[-8000px] top-[-8000px]">{inner}</div>,
        document.body,
    );

    return {
        exportTaskReport,
        taskReportView,
    };
};

export const SingleReport = ({
    taskId,
    onExported = null,
    autoExport = false,
    showExport = false,
}) => {
    const { apiClient, oss } = useContext(Context);
    const [task, setTask] = useState(null);
    const [defections, setDefections] = useState(null);
    const [defDefections, setDefDefections] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const normalizeTaskForReport = (task) => {
        if (!task) {
            return null;
        }

        return {
            ...task,
            turbines: (task.turbines || []).map((turbine) => ({
                ...turbine,
                leafs: turbine.leafs || turbine.blades || [],
            })),
        };
    };
    const init = async () => {
        setLoading(true);
        setError(null);
        try {
            let t1 = initializePdf();
            let t2 = apiClient.getTask(taskId);
            let t3 = listDefectionOfTask(apiClient, taskId);
            let t4 = listDefDefection(apiClient, null);
            let [ok, task, defections, defDefections] = await Promise.all([
                t1,
                t2,
                t3,
                t4,
            ]);
            console.log(
                "task:",
                task,
                "defections:",
                defections,
                "defDefections:",
                defDefections,
            );
            setTask(normalizeTaskForReport(task));
            setDefections(defections || []);
            setDefDefections(defDefections || []);
        } catch (error) {
            console.error("Failed to load report:", error);
            setTask(null);
            setDefections([]);
            setDefDefections([]);
            setError(error);
        } finally {
            setLoading(false);
        }
        // setTimeout(() => {
        //     onComplete?.();
        // }, 3000);
    };
    useEffect(() => {
        if (!apiClient || !taskId) {
            return;
        }
        init();
    }, [apiClient, taskId]);

    return (
        <>
            {loading && (
                <div className="flex flex-row justify-center items-center">
                    Report Loading...
                </div>
            )}
            {!loading && error && (
                <div className="flex flex-row justify-center items-center text-red-600 p-4">
                    报告加载失败：{error.message || "未知错误"}
                </div>
            )}
            {!loading && !error && task && (
                <SingleReportInner
                    task={task}
                    oss={oss}
                    defections={defections}
                    defDefections={defDefections}
                    onExported={onExported}
                    autoExport={autoExport}
                    showExport={showExport}
                />
            )}
        </>
    );
};

const Kv1 = ({ k, v }) => {
    return (
        <div className="my-2 flex flex-row">
            <div className="text-lg">{k}</div>
            <div className="text-lg pr-4 border-b border-[#000]">{v}</div>
        </div>
    );
};
const Kv2 = ({ k, v }) => {
    return (
        <div className="flex flex-row my-3">
            <div className="text-lg font-medium">{k}</div>
            <div className="text-lg flex-1">{v}</div>
        </div>
    );
};
const OnePage = ({ title, children }) => {
    const { pageWidth, pageHeight } = usePdfSize();
    const pageStyle = {
        width: `${pageWidth}pt`,
        minHeight: `${pageHeight}pt`,
        // border: "1px solid #f00",
    };
    return (
        <div className="page break-after-page px-8 py-8" style={pageStyle}>
            <div className="flex flex-row justify-end">
                <img
                    src="/bowongtec-logo.png"
                    alt="上海伯望 Logo"
                    className="h-[48px] "
                />
            </div>
            {title && <div className="text-xl mb-8">{title}</div>}
            {children}
        </div>
    );
};
const Th = ({ children }) => {
    return <div className="font-bold">{children}</div>;
};

const DefectionImage = ({ defection, oss }) => {
    const [frameUrl, setFrameUrl] = useState(null);
    
    useEffect(() => {
        const loadFrameUrl = async () => {
            const fallbackUrl = buildReportImageUrl(defection);
            let url = null;
            if (!fallbackUrl && defection?.from_frame?.id) {
                url = await getFrameDataUrl(defection.from_frame.id);
            }
            setFrameUrl(url || fallbackUrl);
        };
        loadFrameUrl();
    }, [
        defection?.id,
        defection?.from_frame?.id,
        defection?.inspection_image?.file_path,
        defection?.inspection_image?.thumbnail_url,
        defection?.x,
        defection?.y,
        defection?.width,
        defection?.height,
    ]);
    
    return (
        <div className="flex-1">
            <div className="flex flex relative flex-col text-base">
                <table className="my-2 border-collapse border border-slate-400">
                    <tr>
                        <td className="p-1 border border-slate-300">
                            风电场：
                            {
                                defection?.from_frame?.from_raw?.leaf?.turbine
                                    ?.field_item?.name
                            }
                        </td>
                        <td className="p-1 border border-slate-300">
                            风机编号：
                            {
                                defection?.from_frame?.from_raw?.leaf?.turbine
                                    ?.seq
                            }
                        </td>

                        <td className="p-1 border border-slate-300">
                            叶片编号：
                            {defection?.from_frame?.from_raw?.leaf?.seq}
                        </td>
                    </tr>
                    <tr>
                        <td className="p-1 border border-slate-300">
                            缺陷描述：{defection?.label?.name}
                        </td>
                        <td className="p-1 border border-slate-300">
                            叶片位置：
                            {defection?.from_frame?.from_raw?.leaf?.attach_id}
                        </td>
                        <td className="p-1 border border-slate-300">
                            缺陷位置：
                            {formatFloat(defection?.from_frame?.distance)}m
                        </td>
                    </tr>
                </table>
                <div className="relative w-fit h-fit">
                    {frameUrl ? (
                        <img src={frameUrl} className="w-[730px] max-h-[420px] object-contain" />
                    ) : (
                        <div className="w-[730px] h-[360px] flex items-center justify-center bg-slate-100 text-slate-500">
                            图片加载中
                        </div>
                    )}
                    <div
                        style={{
                            position: "absolute",
                            left: "10%",
                            top: "10%",
                            color: "#fff",
                        }}
                    >
                        {mapPart[defection?.from_frame?.from_raw?.part]?.text}
                    </div>
                    {/*
                    <div
                        style={{
                            position: "absolute",
                            left: "10%",
                            bottom: "10%",
                            color: "#fff",
                        }}
                    >
                        {formatFloat(defection?.from_frame?.distance)}m
                    </div>
                    <div
                        style={{
                            position: "absolute",
                            right: "10%",
                            bottom: "10%",
                            color: "#fff",
                        }}
                    >
                        {toDateWeekdayTimeString(defection?.updated_at)}
                    </div>
                    */}
                </div>
            </div>
        </div>
    );
};
const SingleReportInner = ({
    task,
    defections,
    defDefections,
    oss,
    showExport = false,
    autoExport = false,
    onExported = null,
}) => {
    const [exporting, setExporting] = useState(false);
    const defectionCount = useMemo(() => {
        return defections?.length || 0;
    }, [defections]);
    const defDefectionNames = useMemo(() => {
        const configuredNames = (defDefections || [])
            .map((it) => it.name)
            .filter(Boolean);
        const reportedNames = (defections || [])
            .map((it) => it.label?.name)
            .filter(Boolean);
        return _.uniq([...reportedNames, ...configuredNames]);
    }, [defDefections, defections]);
    const defectionCountByType = useMemo(() => {
        return defDefectionNames.map(
            (it) => (defections || []).filter((d) => d.label?.name == it).length,
        );
    }, [defDefectionNames, defections]);
    const turbineSeqs = useMemo(() => {
        return task?.turbines?.map((it) => it.seq) || [];
    }, [task]);

    const defectionsGroupByTurbine = useMemo(() => {
        // defections.map(it => it?.from_frame?.from_raw?.leaf?.turbine?.seq);
        return turbineSeqs.map((turbineSeq) => {
            let defs = defections.filter(
                (it) =>
                    it.from_frame?.from_raw?.leaf?.turbine?.seq == turbineSeq,
            );
            let defectionCount = defs.length;
            let aCount = defs.filter(
                (it) => it?.from_frame?.from_raw?.leaf?.attach_id == "A",
            ).length;
            let bCount = defs.filter(
                (it) => it?.from_frame?.from_raw?.leaf?.attach_id == "B",
            ).length;
            let cCount = defs.filter(
                (it) => it?.from_frame?.from_raw?.leaf?.attach_id == "C",
            ).length;
            return {
                turbineSeq,
                defectionCount,
                aCount,
                bCount,
                cCount,
            };
        });
    }, [turbineSeqs, defections]);

    const defectionsGroupByTurbineColumns = [
        createTableColumn({
            columnId: "turbineSeq",
            renderHeaderCell: () => {
                return <Th>风机编号</Th>;
            },
            renderCell: (item) => {
                return item.turbineSeq;
            },
        }),
        createTableColumn({
            columnId: "defectionCount",
            renderHeaderCell: () => {
                return <Th>缺陷数</Th>;
            },
            renderCell: (item) => {
                return item.defectionCount;
            },
        }),
        createTableColumn({
            columnId: "aCount",
            renderHeaderCell: () => {
                return <Th>叶片A</Th>;
            },
            renderCell: (item) => {
                return item.aCount;
            },
        }),
        createTableColumn({
            columnId: "bCount",
            renderHeaderCell: () => {
                return <Th>叶片B</Th>;
            },
            renderCell: (item) => {
                return item.bCount;
            },
        }),
        createTableColumn({
            columnId: "cCount",
            renderHeaderCell: () => {
                return <Th>叶片C</Th>;
            },
            renderCell: (item) => {
                return item.cCount;
            },
        }),
    ];
    const defectionsGroupByTurbineTable = useMemo(() => {
        return (
            <table className="table-auto w-full my-2 border-collapse border border-slate-400">
                <tr>
                    <th className="p-1 border border-slate-400">风机编号</th>
                    <th className="p-1 border border-slate-400">缺陷数</th>
                    <th className="p-1 border border-slate-400">叶片A</th>
                    <th className="p-1 border border-slate-400">叶片B</th>
                    <th className="p-1 border border-slate-400">叶片C</th>
                </tr>

                {defectionsGroupByTurbine.map((turbine) => {
                    return (
                        <tr>
                            <td className="p-1 border border-slate-400">
                                {turbine.turbineSeq}
                            </td>
                            <td className="p-1 border border-slate-400">
                                {turbine.defectionCount}
                            </td>
                            <td className="p-1 border border-slate-400">
                                {turbine.aCount}
                            </td>
                            <td className="p-1 border border-slate-400">
                                {turbine.bCount}
                            </td>
                            <td className="p-1 border border-slate-400">
                                {turbine.cCount}
                            </td>
                        </tr>
                    );
                })}
            </table>
        );
    }, [defectionsGroupByTurbine]);
    const defectionDistributionData = useMemo(() => {
        return task.turbines?.map((turbine) => {
            const leafs = turbine.leafs || [];
            let leafRows = leafs.map((leaf, i) => {
                let countings = defDefectionNames.map((defectionName) => {
                    let defectionsOfLeaf = defections.filter(
                        (it) =>
                            it.from_frame?.from_raw?.leaf?.turbine?.seq ==
                                turbine.seq &&
                            it.from_frame?.from_raw?.leaf?.seq == leaf?.seq &&
                            it.label?.name == defectionName,
                    );
                    let count = defectionsOfLeaf.length;
                    return (
                        <td className="p-1 border border-slate-400 text-center">
                            {count}
                        </td>
                    );
                });
                return (
                    <tr>
                        {i === 0 && (
                            <td
                                rowSpan={leafs.length}
                                className="p-1 border border-slate-400"
                            >
                                {turbine.seq}
                            </td>
                        )}
                        <td className="p-1 border border-slate-400">
                            {leaf?.seq}
                        </td>
                        {countings}
                    </tr>
                );
            });
            return leafRows;
        });
    }, [task, defections, defDefectionNames]);

    const defectionDistributionHeader = useMemo(() => {
        const hs = defDefectionNames.map((n) => (
            <th className="p-1 border border-slate-400 text-center">{n}</th>
        ));
        return (
            <tr>
                <th className="p-1 border border-slate-400">风机编号</th>
                <th className="p-1 border border-slate-400">叶片编号</th>
                {hs}
            </tr>
        );
    }, [defDefectionNames]);

    const defectionDistribution = useMemo(() => {
        return (
            <table className="table-auto w-full my-2 border-collapse border border-slate-400">
                {defectionDistributionHeader}
                {defectionDistributionData}
            </table>
        );
    }, [defectionDistributionHeader, defectionDistributionData]);

    const defectionsGroupByType = useMemo(() => {
        return defDefectionNames.map((label) => {
            let defs = defections.filter((it) => it.label?.name == label);
            let turbineCount = _.uniqBy(
                defs,
                (it) => it.from_frame?.from_raw?.leaf?.turbine?.id,
            ).length;
            let leafSeqs = _.uniq(
                defs.map((it) => it.from_frame?.from_raw?.leaf?.seq),
            ).join(", ");
            return {
                label,
                turbineCount,
                leafSeqs,
            };
        });
    }, [defDefectionNames, defections]);
    const defectionsGroupByTypeColumns = [
        createTableColumn({
            columnId: "label",
            renderHeaderCell: () => {
                return <Th>缺陷类型</Th>;
            },
            renderCell: (item) => {
                return item.label;
            },
        }),
        createTableColumn({
            columnId: "turbineCount",
            renderHeaderCell: () => {
                return <Th>风机数</Th>;
            },
            renderCell: (item) => {
                return item.turbineCount;
            },
        }),
        createTableColumn({
            columnId: "leafSeqs",
            renderHeaderCell: () => {
                return <Th>风机叶片编号</Th>;
            },
            renderCell: (item) => {
                return item.leafSeqs;
            },
        }),
    ];

    const waitForNextPaint = () =>
        new Promise((resolve) => {
            requestAnimationFrame(() => requestAnimationFrame(resolve));
        });

    const doExportPdf = async () => {
        if (exporting) {
            return;
        }

        const el = document.getElementById("single_report");
        if (!el) {
            return;
        }

        try {
            el.style.fontFamily = "yahei";
            setExporting(true);
            await waitForNextPaint();

            let finished = false;
            const finish = () => {
                if (finished) {
                    return;
                }

                finished = true;
                setExporting(false);
                onExported?.();
                window.removeEventListener("afterprint", finish);
            };

            window.addEventListener("afterprint", finish);
            window.print();

            // Some browsers do not reliably fire afterprint when the dialog is cancelled.
            window.setTimeout(finish, 3000);
        } catch (error) {
            console.error("Failed to export report PDF:", error);
            setExporting(false);
            window.alert("导出PDF失败，请稍后重试。");
        }
    };
    const init = () => {
        const option = {
            backgroundColor: "#fff",
            xAxis: {
                // data: ["鼓包", "裂纹", "褶皱", "透光", "泛白", "其它"],
                data: defDefectionNames,
            },
            yAxis: {},
            series: [
                {
                    name: "缺陷数量",
                    type: "bar",
                    data: defectionCountByType,
                    barWidth: 10,
                    itemStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 1, 0, [
                            {
                                offset: 0,
                                color: "#1890FF",
                            },
                            {
                                offset: 1,
                                color: "#1EE7E7",
                            },
                        ]),
                    },
                },
            ],
        };

        let el = document.getElementById("defectionChart");
        if (!el) {
            console.log("chart can find dom:", el);
            return;
        }
        const chart = echarts.getInstanceByDom(el) || echarts.init(el);
        chart.setOption(option, true);
        setTimeout(() => {
            onReady();
        }, 1000);
    };
    const onReady = () => {
        if (autoExport) {
            doExportPdf();
        }
    };

    useEffect(() => {
        init();
        // setTimeout(init, 100);
    }, [defDefectionNames, defectionCountByType]);
    const { pageWidth, pageHeight } = usePdfSize();
    const pageStyle = {
        width: `${pageWidth}pt`,
        height: `${pageHeight}pt`,
        // border: "1px solid #ccc",
    };
    const styles = useStyles();
    return (
        <div
            id="single_report"
            className="flex flex-col font-[Microsoft_Yahei]"
        >
            <div
                className="flex flex-col page break-after-page"
                style={pageStyle}
            >
                <div className="flex flex-row">
                    <div id="pageWidth" className="hidden">
                        {pageWidth}
                    </div>
                    <div id="defectionCount" className="hidden">
                        {defectionCount}
                    </div>
                    <img
                        src="/bowongtec-logo.png"
                        alt="上海伯望 Logo"
                        className="my-8 ml-12 h-[72px]"
                    />
                    <div className="flex-1"></div>
                    {showExport && !exporting && (
                        <div className="print-hidden">
                            <Button
                                appearance="subtle"
                                onClick={() => doExportPdf()}
                            >
                                导出pdf
                            </Button>
                        </div>
                    )}
                </div>
                <img src="/background.jpeg" className="object-contain block" />
                <div className="text-[#003039] text-4xl text-right mr-1 mt-4  mb-4 font-medium">
                    风电叶片外部
                </div>
                <div className="text-[#605E5C] text-4xl text-right mr-1 font-medium">
                    巡检报告
                </div>
                <div className="flex-1 flex flex-col ml-16 justify-center">
                    <Kv1
                        k="工作地点："
                        v={task?.turbines?.[0]?.field_item?.name}
                    />
                    <Kv1 k="报告编号：" v={task?.seq} />
                    <Kv1 k="任务名称：" v={task?.name} />
                    <Kv1
                        k="工作完成日期："
                        v={yyyymmddCn(task?.completed_at)}
                    />
                    <Kv1
                        k="报告提交日期："
                        v={yyyymmddCn(task?.completed_at)}
                    />
                    <div className="flex mt-8">
                        <Kv1 k="工作负责人：" v={task?.owner_checker?.name} />
                    </div>
                    <div className="flex mt-4">
                        <Kv1 k="复核人：" v={task?.owner_rechecker?.name} />
                    </div>
                </div>
                {/*
                <div className="text-[10px] text-center mb-4">
                    上海发电设备成套设计研究院有限责任公司
                </div>
                */}
            </div>
            <OnePage>
                <div className="mb-4 text-2xl font-semibold">一、报告概述</div>
                <div className="px-4">
                    <div className="text-lg font-medium py-2">
                        （一）基本信息
                    </div>
                    <div className="px-6">
                        <Kv2 k="任务名称：" v={task?.name} />
                        <Kv2
                            k="风场名称："
                            v={task?.turbines?.[0]?.field_item?.name}
                        />
                        <Kv2 k="巡检机数：" v={task?.turbines?.length} />
                        <Kv2 k="优先级别：" v={cvtPriority(task?.priority)} />
                    </div>
                </div>
                <div className="px-4 py-8">
                    <div className="text-lg font-medium py-2">
                        （二）巡检内容
                    </div>
                    <div className="px-6">
                        <Kv2
                            k="巡检范围："
                            v={"风机三个叶片内腔的前缘、腹板和后缘部位"}
                        />
                        <Kv2
                            k="巡检要求："
                            v={"风机停机，并将叶片调整到指定位置"}
                        />
                        <Kv2
                            k="巡检操作："
                            v={
                                "风机状态达到巡检要求后，操作员通过控制器操作检测机器人，从叶片内腔根部开始扫描，并生成高清视频文件。"
                            }
                        />
                        <Kv2
                            k="缺陷复核："
                            v={
                                "全量复核，复核人员检查生成的所有图像信息，以及AI算法自动识别的缺陷记录，判断叶片内腔是否有缺陷。若与算法识别结果有出入，则通过系统界面修改缺陷记录。"
                            }
                        />
                    </div>
                </div>
            </OnePage>
            <OnePage>
                <div className="mb-4 text-2xl font-semibold">二、缺陷汇总</div>
                <div className="text-lg font-medium py-2">
                    （一）风场缺陷数量统计
                </div>
                <div className="px-4">
                    <div className="flex flex-row bg-white">
                        <div className="flex flex-col justify-center items-center px-8 py-12">
                            <div className="text-xs py-2 whitespace-nowrap">
                                缺陷总数量
                            </div>
                            <div className="text-4xl text-[#D83D01]">
                                {defectionCount}
                            </div>
                        </div>
                        <div
                            id="defectionChart"
                            className="flex-1 h-[180px] bg-white"
                        ></div>
                    </div>
                </div>
                <div className="text-lg font-medium py-2">
                    （二）风机缺陷数量统计
                </div>
                <div className="px-4 py-8">
                    <div className="pl-6">
                        {/*
                        <SimpleGrid
                            className={styles.grid}
                            items={defectionsGroupByTurbine}
                            columns={defectionsGroupByTurbineColumns}
                        />
                     */}
                        {defectionsGroupByTurbineTable}
                    </div>
                </div>
            </OnePage>
            <OnePage>
                <div className="mb-4 text-2xl font-semibold">
                    三、缺陷类型分布
                </div>
                <div className="flex flex-1 px-4">
                    {/*
                    <SimpleGrid
                        className={styles.grid}
                        items={defectionsGroupByType}
                        columns={defectionsGroupByTypeColumns}
                    />
                    */}
                    {defectionDistribution}
                </div>
            </OnePage>
            <OnePage>
                <div className="mb-4 text-2xl font-semibold">四、缺陷详情</div>
                {turbineSeqs.map((turbineSeq) => {
                    let defs = defections.filter(
                        (it) =>
                            it.from_frame?.from_raw?.leaf?.turbine?.seq ===
                            turbineSeq,
                    );
                    if (defs.length === 0) {
                        return null;
                    }
                    let _leafs = ["A", "B", "C"].map((attachId) => {
                        let _defs = defs.filter(
                            (it) =>
                                it.from_frame?.from_raw?.leaf?.attach_id ===
                                attachId,
                        );
                        if (_defs.length === 0) {
                            return null;
                        }
                        let leafSeq =
                            _defs?.[0]?.from_frame?.from_raw?.leaf?.seq;
                        // let leafTitle = (
                        //     <div className="text-xs font-bold my-2">
                        //         叶片{attachId}-{leafSeq}
                        //     </div>
                        // );
                        // let groupLen = Math.ceil(_defs.length / 2);
                        _defs = Array.from(
                            { length: _defs.length },
                            (_, i) => i,
                        ).map((i) => {
                            let d = _defs[i];
                            return (
                                <div
                                    className="no-page-break-inside flex flex-row gap-8 mt-8"
                                    key={i}
                                >
                                    <DefectionImage
                                        key={idString(d.id)}
                                        defection={d}
                                        oss={oss}
                                    />
                                </div>
                            );
                        });
                        return (
                            <div key={attachId} className="my-2">
                                {_defs}
                            </div>
                        );
                    });
                    return (
                        <div key={turbineSeq} className="mb-2">
                            {_leafs}
                        </div>
                    );
                })}
            </OnePage>
        </div>
    );
};
